<template>
  <section class="review-awards" :class="{ desktop: $isDesktop }">
    <div
      v-observe-visibility="{
        callback: heroVisibilityChanged,
        throttle: 300,
        throttleOptions: {
          leading: 'hidden',
        },
      }"
      class="hero"
    >
      <div>
        <h1>Pitchup.com <br />Review Awards <br />2024</h1>
        <review-awards-pages-nav />
      </div>
    </div>

    <nav ref="page-navigation" :class="{ pinned: navigationOffscreen }">
      <ul class="nav-list">
        <li><a href="#how-it-works">How it works</a></li>
        <li><a href="#overall-winner">Overall winner</a></li>
        <li><a href="#runner-up">Runner up</a></li>
        <li><a href="#golds">Camping & Glamping Golds</a></li>
        <li><a href="#regional-winners">Regional winners</a></li>
        <li><a href="#top-rated-by-theme">Top rated by theme</a></li>
      </ul>
    </nav>

    <section id="how-it-works">
      <div class="intro">
        <p>
          We're thrilled to share our shortlist of the best camping, glamping
          and caravan sites based on nearly 200,000 customer reviews last year.
        </p>
        <p>
          To feature in the list, sites must have achieved an average rating of
          8 or above across at least 20 reviews in 2024. We keep the criteria
          simple and rely solely on verified customer feedback to ensure the
          list is unbiased and reflects real user experiences.
        </p>
        <p>
          We want to say a huge congratulations to the winning sites. Their
          outstanding average rating is the result of months of glowing reviews
          on Pitchup.com, showing that they provided a truly exceptional guest
          experience.
        </p>
      </div>
      <div v-if="$isDesktop" class="map-wrapper">
        <UkRegionsMap />
      </div>
    </section>

    <section id="overall-winner" class="campsite-card">
      <h2>Our top-rated campsite</h2>
      <a
        class="campsite winner"
        target="_blank"
        href="https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/Skipton/draughton-heights-campsite/"
      >
        <div class="cs-img">
          <img
            loading="lazy"
            width="500"
            height="auto"
            alt="Draughton Heights Campsite - Overall winner"
            src="https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/3/image/private/s--1FAcswH3--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1710935060/draughton-heights-campsite/1367376.jpg"
            :sizes="$isDesktop ? '640px' : '88vw'"
            :srcset="`https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/3/image/private/s--1FAcswH3--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1710935060/draughton-heights-campsite/1367376.jpg 800w, https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=1200,h=900/images/3/image/private/s--1FAcswH3--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1710935060/draughton-heights-campsite/1367376.jpg 1200w`"
          />
        </div>
        <div class="contents">
          <img
            src="@/assets/images/first-place.png"
            alt=""
            width="100"
            height="140"
            class="rosette"
            loading="lazy"
          />
          <img
            src="@/assets/images/pitchup-logo.png"
            alt=""
            width="80"
            height="70"
            loading="lazy"
          />
          <h2>Draughton Heights Campsite</h2>
          <p>
            Scoring 10/10 from a whopping 251 reviews, Draughton Heights
            Campsite is the best of the best. Open year-round and on the
            southern fringes of the Yorkshire Dales National Park, it's a site
            that's popular with hikers and families alike. Showered in praise
            for its immaculate Yorkshire stone amenities block, the site leaves
            customers itching to return, with 100% saying they would stay there
            again.
          </p>
        </div>
      </a>
      <aside>
        <div class="triangle" />
        <img
          src="@/assets/images/quotemark-66.png"
          alt=""
          width="60"
          height="41"
          class="quotemark"
          loading="lazy"
        />
        <span>
          <p>
            It feels amazing to win this award. I'm so proud of everyone that's
            helped us get this far. We're absolutely chuffed.
          </p>
          <p>
            We are a unique site. We have amazing views, we listen to what
            people want and we spend time with them. We treat them as family,
            giving them all individual attention. I think this is what clinched
            it for us.
          </p>
        </span>
        <span>
          <p>
            Without being too soppy, I'd like to thank my husband as it was his
            baby at the start. I don't know where he got the idea from, but he's
            done an amazing job. He lives and breathes the campsite and I'm
            grateful to have the opportunity to manage what he's built. And of
            course, thanks to Pitchup for supporting us and sending us so many
            bookings!
          </p>
          <p class="author">Lesley</p>
        </span>
        <img
          src="@/assets/images/quotemark-99.png"
          alt=""
          width="60"
          height="41"
          class="quotemark close"
          loading="lazy"
        />
      </aside>
    </section>

    <section id="runner-up" class="campsite-card">
      <h2>Runner up</h2>
      <a
        href="https://www.pitchup.com/campsites/Wales/North-Wales/Gwynedd/Criccieth/the-paddocks-caravan-and-camping/"
        target="_blank"
        class="campsite runner-up with-border"
      >
        <div class="cs-img">
          <img
            loading="lazy"
            width="500"
            height="auto"
            alt="The Paddocks Caravan and Camping - Runner up"
            src="https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/1/image/private/s--W9LtBNpr--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1661172434/the-paddocks-caravan-and-camping/1044540.jpg"
            :sizes="$isDesktop ? '640px' : '88vw'"
            :srcset="`https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/1/image/private/s--W9LtBNpr--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1661172434/the-paddocks-caravan-and-camping/1044540.jpg 800w, https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=1200,h=900/images/1/image/private/s--W9LtBNpr--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1661172434/the-paddocks-caravan-and-camping/1044540.jpg 1200w`"
          />
        </div>
        <div class="contents">
          <img
            src="@/assets/images/second-place.png"
            alt=""
            width="100"
            height="140"
            class="rosette"
            loading="lazy"
          />
          <img
            src="@/assets/images/pitchup-logo.png"
            alt=""
            width="80"
            height="70"
            loading="lazy"
          />
          <h2>The Paddocks Caravan and Camping</h2>
          <p>
            Scoring 10/10 from 143 reviews, this Gwynedd site is a close second
            place. This small family-run site is ultra-organised and
            well-maintained. Five minutes' drive to the beach and just a
            five-minute stroll to the local village and pub, it's perfectly
            situated for a peaceful break. Guests particularly loved the warm
            welcome and spacious pitches.
          </p>
        </div>
      </a>
    </section>

    <section id="golds" class="top-5">
      <div class="golds-heading">
        <img
          loading="lazy"
          src="@/assets/images/pitchup-favicon.svg"
          alt=""
          width="36"
          height="32"
        />
        <h2>Camping & Glamping Golds</h2>
      </div>
      <p class="golds-intro">
        A new category for 2025, these sites are the cream of the crop, each
        achieving a perfect 10/10 score from at least 20 reviews in the last
        year.
      </p>
      <div class="top-5-data">
        <review-awards-top-sites :data="TopSitesUKData" />
      </div>
    </section>

    <RegionalWinners id="regional-winners" :data="RegionalWinnersUKData" />

    <section id="top-rated-by-theme" class="top-theme">
      <section class="campsite-card">
        <h2>Top-rated sites by theme</h2>
        <a
          target="_blank"
          href="https://www.pitchup.com/campsites/England/South_West/Dorset/Weymouth/east-fleet-farm-touring-park/"
          class="campsite with-border"
        >
          <div class="cs-img">
            <img
              loading="lazy"
              width="500"
              height="auto"
              alt="East Fleet Farm Touring Park - The most peaceful campsite"
              src="https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/5/image/private/s--ivInaUX8--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1432118744/east-fleet-farm-touring-park/83705.jpg"
              :sizes="$isDesktop ? '640px' : '88vw'"
              :srcset="`https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/5/image/private/s--ivInaUX8--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1432118744/east-fleet-farm-touring-park/83705.jpg 800w, https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=1200,h=900/images/5/image/private/s--ivInaUX8--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1432118744/east-fleet-farm-touring-park/83705.jpg 1200w`"
            />
          </div>
          <div class="contents">
            <h2>The most peaceful campsite</h2>
            <p>
              <strong>East Fleet Farm Touring Park</strong> was dubbed
              'peaceful' by a whopping 245 reviewers in 2024, more than any
              other site. Wonderfully spacious, with sea views, this coastal
              retreat has everything you need for a stress-free break, including
              a well-stocked shop and restaurant.
            </p>
          </div>
        </a>

        <a
          target="_blank"
          href="https://www.pitchup.com/campsites/England/Central/Shropshire/Shrewsbury/stoney-acres-luxury-holiday-park/"
          class="campsite with-border"
        >
          <div class="cs-img">
            <img
              loading="lazy"
              width="500"
              height="auto"
              alt="Stoney Acres Luxury Holiday Park - The most luxurious campsite"
              src="https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/4/image/private/s--SCAUZp44--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1702891125/stoney-acres-luxury-holiday-park/1332426.jpg"
              :sizes="$isDesktop ? '640px' : '88vw'"
              :srcset="`https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/4/image/private/s--SCAUZp44--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1702891125/stoney-acres-luxury-holiday-park/1332426.jpg 800w, https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=1200,h=900/images/4/image/private/s--SCAUZp44--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1702891125/stoney-acres-luxury-holiday-park/1332426.jpg 1200w`"
            />
          </div>
          <div class="contents">
            <h2>The most luxurious campsite</h2>
            <p>
              The winner of the most luxurious site in 2024, was
              <strong>Stoney Acres Luxury Holiday Park</strong>, with 59 people
              referring to it as 'luxury' in their reviews. Reviewers found its
              name totally justified, praising the immaculately kept pitches,
              grounds and facilities, free tumble dryer and complimentary tea,
              coffee and biscuits. It's no surprise that this site has nabbed
              the top spot.
            </p>
          </div>
        </a>

        <a
          target="_blank"
          href="https://www.pitchup.com/campsites/England/Central/Derbyshire/Bakewell/haddon-grove-farm-camp-site/"
          class="campsite with-border"
        >
          <div class="cs-img">
            <img
              loading="lazy"
              width="500"
              height="auto"
              alt="Haddon Grove Farm Campsite - The best on a budget"
              src="https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/5/image/private/s--MZzuOXEE--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1677078058/haddon-grove-farm-camp-site/1121690.jpg"
              :sizes="$isDesktop ? '640px' : '88vw'"
              :srcset="`https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/5/image/private/s--MZzuOXEE--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1677078058/haddon-grove-farm-camp-site/1121690.jpg 800w, https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=1200,h=900/images/5/image/private/s--MZzuOXEE--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1677078058/haddon-grove-farm-camp-site/1121690.jpg 1200w`"
            />
          </div>
          <div class="contents">
            <h2>The best on a budget</h2>
            <p>
              <strong>Haddon Grove Farm Campsite</strong> was named a perfect
              site for budget-conscious backpackers for the second year in a
              row! Securing 102 votes last year, customers praised the
              great-value pitches, relaxed 'down-to-earth' feel, and easy access
              to the Derbyshire Dales.
            </p>
          </div>
        </a>
      </section>
    </section>
    <nuxt-to-meta :nuxt2meta="head" />
  </section>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import UkRegionsMap from '~/apps/maps/components/UkRegionsMap.vue'
import RegionalWinners from '~/components/reviews/RegionalWinners.vue'
import SCHEMA_ORGANIZATION from '../../json/schemaOrganization.json'

definePageMeta({
  layout: 'default',
})

const { $t, $isDesktop } = useNuxtApp()

// Reactive data
const navigationOffscreen = ref(false)

// Methods
const heroVisibilityChanged = (isVisible: boolean) => {
  navigationOffscreen.value = !isVisible
}

// Computed properties
const title = computed(() => {
  return $t('Pitchup.com review awards')
})

const description = computed(() => {
  return $t(
    "Pitchup.com's review awards are based on nearly 200,000 customer reviews from 2024. Here's our shortlist of the top rated campsites.",
  )
})

const schemaOrganization = computed(() => {
  return {
    type: 'application/ld+json',
    body: true,
    json: SCHEMA_ORGANIZATION,
    hid: 'schema-organization',
  }
})

const head = computed(() => {
  const images = import.meta.glob<{ default: string }>(
    '@/assets/images/review-awards-2024-og.png',
    {
      query: '?url',
      eager: true,
    },
  )
  return {
    htmlAttrs: {
      class: 'smooth-scroll',
    },
    title: title.value,
    script: [schemaOrganization.value],
    meta: [
      {
        hid: 'og:title',
        property: 'og:title',
        content: title.value,
      },
      {
        hid: 'og:url',
        property: 'og:url',
        content: 'https://www.pitchup.com/review-awards/2024/',
      },
      {
        hid: 'description',
        name: 'description',
        property: 'og:description',
        content: description.value,
      },
      {
        hid: 'og:image',
        property: 'og:image',
        content: images['/assets/images/review-awards-2024-og.png'].default,
      },
      {
        hid: 'og:image:width',
        property: 'og:image:width',
        content: '1200',
      },
      {
        hid: 'og:image:height',
        property: 'og:image:height',
        content: '630',
      },
    ],
  }
})

// Data arrays
const TopSitesUKData = [
  {
    name: 'Draughton Heights Campsite',
    region: 'North East England',
    url: '/campsites/England/North_East/North_Yorkshire/Skipton/draughton-heights-campsite/',
    rating: 10,
    reviewsCount: 251,
    pitchTypes: [
      {
        pitchTypesCount: 30,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
      {
        pitchTypesCount: 3,
        name: 'Lodges, cabins, pods or huts',
        slug: 'lodges-for-hire',
        id: '7',
      },
    ],
  },
  {
    name: 'The Paddocks Caravan and Camping',
    region: 'North Wales',
    url: '/campsites/Wales/North-Wales/Gwynedd/Criccieth/the-paddocks-caravan-and-camping/',
    rating: 10,
    reviewsCount: 143,
    pitchTypes: [
      {
        pitchTypesCount: 5,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
      {
        pitchTypesCount: 5,
        name: 'Touring caravan pitches',
        slug: 'touring-pitches',
        id: '3',
      },
    ],
  },
  {
    name: 'Forest of Dean Camping',
    region: 'Central England',
    url: '/campsites/England/Central/Gloucestershire/Lydney/forest-of-dean-camping/',
    rating: 10,
    reviewsCount: 139,
    pitchTypes: [
      {
        pitchTypesCount: 15,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
      {
        pitchTypesCount: 7,
        name: 'Touring caravan pitches',
        slug: 'touring-pitches',
        id: '3',
      },
      {
        pitchTypesCount: 7,
        name: 'Motorhome pitches',
        slug: 'motorhomes',
        id: '10',
      },
    ],
  },
  {
    name: 'Street Farm Caravan Park',
    region: 'North East England',
    url: '/campsites/England/North_East/East_Yorkshire/stamford-bridge/street-farm-caravan-park/',
    rating: 10,
    reviewsCount: 85,
    pitchTypes: [
      {
        pitchTypesCount: 10,
        name: 'Touring caravan pitches',
        slug: 'touring-pitches',
        id: '3',
      },
      {
        pitchTypesCount: 10,
        name: 'Motorhome pitches',
        slug: 'motorhomes',
        id: '10',
      },
    ],
  },
  {
    name: 'Sunnyside Campsite',
    region: 'East Anglia',
    url: '/campsites/England/East_Anglia/Suffolk/cox-common/sunnyside-campsite/',
    rating: 10,
    reviewsCount: 75,
    pitchTypes: [
      {
        pitchTypesCount: 9,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
      {
        pitchTypesCount: 3,
        name: 'Touring caravan pitches',
        slug: 'touring-pitches',
        id: '3',
      },
      {
        pitchTypesCount: 1,
        name: 'Rent a tent',
        slug: 'rent-a-tent',
        id: '13',
      },
    ],
  },
  {
    name: 'Strattons Farm Campsite',
    region: 'East Anglia',
    url: '/campsites/England/East_Anglia/Norfolk/King_s_Lynn/strattons-farm-campsite/',
    rating: 10,
    reviewsCount: 69,
    pitchTypes: [
      {
        pitchTypesCount: 15,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
      {
        pitchTypesCount: 15,
        name: 'Touring caravan pitches',
        slug: 'touring-pitches',
        id: '3',
      },
      {
        pitchTypesCount: 15,
        name: 'Motorhome pitches',
        slug: 'motorhomes',
        id: '10',
      },
    ],
  },
  {
    name: 'Westgate Carr Farm',
    region: 'North East England',
    url: '/campsites/England/North_East/North_Yorkshire/Pickering/westgate-carr-farm/',
    rating: 10,
    reviewsCount: 59,
    pitchTypes: [
      {
        pitchTypesCount: 2,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
      {
        pitchTypesCount: 20,
        name: 'Touring caravan pitches',
        slug: 'touring-pitches',
        id: '3',
      },
      {
        pitchTypesCount: 6,
        name: 'Motorhome pitches',
        slug: 'motorhomes',
        id: '10',
      },
      {
        pitchTypesCount: 1,
        name: 'Lodges, cabins, pods or huts',
        slug: 'lodges-for-hire',
        id: '7',
      },
    ],
  },
  {
    name: 'Quarry Hill Campsite',
    region: 'North East England',
    url: '/campsites/England/North_East/North_Yorkshire/Filey/quarry-hill-campsite/',
    rating: 10,
    reviewsCount: 59,
    pitchTypes: [
      {
        pitchTypesCount: 34,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
      {
        pitchTypesCount: 4,
        name: 'Motorhome pitches',
        slug: 'motorhomes',
        id: '10',
      },
      {
        pitchTypesCount: 9,
        name: 'Rent a tent',
        slug: 'rent-a-tent',
        id: '13',
      },
    ],
  },
  {
    name: 'Little Nash Woods Camping',
    region: 'South East England',
    url: '/campsites/England/South_East/Kent/Canterbury/little-nash-woods-camping/',
    rating: 10,
    reviewsCount: 57,
    pitchTypes: [
      {
        pitchTypesCount: 11,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
    ],
  },
  {
    name: 'Hill Farm Camping',
    region: 'South East England',
    url: '/campsites/England/South_East/Essex/Braintree/hill-farm-camping/',
    rating: 10,
    reviewsCount: 51,
    pitchTypes: [
      {
        pitchTypesCount: 15,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
      {
        pitchTypesCount: 4,
        name: 'Touring caravan pitches',
        slug: 'touring-pitches',
        id: '3',
      },
      {
        pitchTypesCount: 4,
        name: 'Motorhome pitches',
        slug: 'motorhomes',
        id: '10',
      },
    ],
  },
  {
    name: 'Pear Tree Cottage at Poplar Farm',
    region: 'Central England',
    url: '/campsites/England/Central/Lincolnshire/Sutton_on_Sea/pear_tree_cottage_poplar_farm/',
    rating: 10,
    reviewsCount: 45,
    pitchTypes: [
      {
        pitchTypesCount: 2,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
      {
        pitchTypesCount: 2,
        name: 'Touring caravan pitches',
        slug: 'touring-pitches',
        id: '3',
      },
    ],
  },
  {
    name: 'Combas Farm',
    region: 'South West England',
    url: '/campsites/England/South_West/Devon/Croyde/combas-farm/',
    rating: 10,
    reviewsCount: 43,
    pitchTypes: [
      {
        pitchTypesCount: 32,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
    ],
  },
  {
    name: 'Sychpwll Centre',
    region: 'Mid Wales',
    url: '/campsites/Wales/Mid-Wales/Powys/llandrinio/sychpwll-centre/',
    rating: 10,
    reviewsCount: 41,
    pitchTypes: [
      {
        pitchTypesCount: 20,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
      {
        pitchTypesCount: 5,
        name: 'Motorhome pitches',
        slug: 'motorhomes',
        id: '10',
      },
      {
        pitchTypesCount: 5,
        name: 'Lodges, cabins, pods or huts',
        slug: 'lodges-for-hire',
        id: '7',
      },
      {
        pitchTypesCount: 1,
        name: 'Rent a tent',
        slug: 'rent-a-tent',
        id: '13',
      },
    ],
  },
  {
    name: 'Woodland Gardens Caravan Site',
    region: 'Scotland',
    url: '/campsites/Scotland/Scotland/Fife/Leven/woodland-gardens-caravan-site/',
    rating: 10,
    reviewsCount: 38,
    pitchTypes: [
      {
        pitchTypesCount: 11,
        name: 'Touring caravan pitches',
        slug: 'touring-pitches',
        id: '3',
      },
      {
        pitchTypesCount: 11,
        name: 'Motorhome pitches',
        slug: 'motorhomes',
        id: '10',
      },
      {
        pitchTypesCount: 5,
        name: 'Lodges, cabins, pods or huts',
        slug: 'lodges-for-hire',
        id: '7',
      },
      {
        pitchTypesCount: 1,
        name: 'Caravans for hire',
        slug: 'caravans-for-hire',
        id: '6',
      },
    ],
  },
  {
    name: 'Scar Close Camping and Glamping',
    region: 'North West England',
    url: '/campsites/England/North_West/Lancashire/warton/scar-close-camping-glamping/',
    rating: 10,
    reviewsCount: 31,
    pitchTypes: [
      {
        pitchTypesCount: 5,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
      {
        pitchTypesCount: 2,
        name: 'Lodges, cabins, pods or huts',
        slug: 'lodges-for-hire',
        id: '7',
      },
      {
        pitchTypesCount: 3,
        name: 'Rent a tent',
        slug: 'rent-a-tent',
        id: '13',
      },
    ],
  },
  {
    name: 'Hidden Hideaway',
    region: 'Central England',
    url: '/campsites/England/Central/Leicestershire/Market_Harborough/hidden-hideaway/',
    rating: 10,
    reviewsCount: 29,
    pitchTypes: [
      {
        pitchTypesCount: 5,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
      {
        pitchTypesCount: 5,
        name: 'Touring caravan pitches',
        slug: 'touring-pitches',
        id: '3',
      },
      {
        pitchTypesCount: 5,
        name: 'Motorhome pitches',
        slug: 'motorhomes',
        id: '10',
      },
      {
        pitchTypesCount: 2,
        name: 'Rent a tent',
        slug: 'rent-a-tent',
        id: '13',
      },
    ],
  },
  {
    name: 'Field View Luxury Glamping',
    region: 'South East England',
    url: '/campsites/England/South_East/Hampshire/alton/field-view-luxury-glamping/',
    rating: 10,
    reviewsCount: 28,
    pitchTypes: [
      {
        pitchTypesCount: 2,
        name: 'Rent a tent',
        slug: 'rent-a-tent',
        id: '13',
      },
    ],
  },
  {
    name: 'Green Haven Camping',
    region: 'East Anglia',
    url: '/campsites/England/East_Anglia/Suffolk/rumburgh/green-haven-camping/',
    rating: 10,
    reviewsCount: 26,
    pitchTypes: [
      {
        pitchTypesCount: 9,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
      {
        pitchTypesCount: 5,
        name: 'Touring caravan pitches',
        slug: 'touring-pitches',
        id: '3',
      },
      {
        pitchTypesCount: 5,
        name: 'Motorhome pitches',
        slug: 'motorhomes',
        id: '10',
      },
      {
        pitchTypesCount: 1,
        name: 'Lodges, cabins, pods or huts',
        slug: 'lodges-for-hire',
        id: '7',
      },
    ],
  },
  {
    name: 'Lindsey Trail Touring Park',
    region: 'Central England',
    url: '/campsites/England/Central/Lincolnshire/Market_Rasen/lindsey_trail_touring_park/',
    rating: 10,
    reviewsCount: 26,
    pitchTypes: [
      {
        pitchTypesCount: 24,
        name: 'Touring caravan pitches',
        slug: 'touring-pitches',
        id: '3',
      },
      {
        pitchTypesCount: 24,
        name: 'Motorhome pitches',
        slug: 'motorhomes',
        id: '10',
      },
    ],
  },
  {
    name: 'Orchard Leaze Holiday Park',
    region: 'Central England',
    url: '/campsites/England/Central/Lincolnshire/Alford/orchard-leaze-holiday-park/',
    rating: 10,
    reviewsCount: 25,
    pitchTypes: [
      {
        pitchTypesCount: 2,
        name: 'Touring caravan pitches',
        slug: 'touring-pitches',
        id: '3',
      },
      {
        pitchTypesCount: 8,
        name: 'Lodges, cabins, pods or huts',
        slug: 'lodges-for-hire',
        id: '7',
      },
      {
        pitchTypesCount: 2,
        name: 'Caravans for hire',
        slug: 'caravans-for-hire',
        id: '6',
      },
    ],
  },
  {
    name: 'Fairoak Lakes',
    region: 'South Wales',
    url: '/campsites/Wales/South-Wales/Monmouthshire/Chepstow/fairoak-lakes/',
    rating: 10,
    reviewsCount: 25,
    pitchTypes: [
      {
        pitchTypesCount: 2,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
      {
        pitchTypesCount: 4,
        name: 'Lodges, cabins, pods or huts',
        slug: 'lodges-for-hire',
        id: '7',
      },
    ],
  },
  {
    name: 'New Farm Holidays',
    region: 'Central England',
    url: '/campsites/England/Central/Lincolnshire/Boston/new-farm-holidays/',
    rating: 10,
    reviewsCount: 24,
    pitchTypes: [
      {
        pitchTypesCount: 8,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
      {
        pitchTypesCount: 5,
        name: 'Lodges, cabins, pods or huts',
        slug: 'lodges-for-hire',
        id: '7',
      },
    ],
  },
  {
    name: 'Rockridge',
    region: 'Central England',
    url: '/campsites/England/Central/Worcestershire/Worcester/rockridge/',
    rating: 10,
    reviewsCount: 24,
    pitchTypes: [
      {
        pitchTypesCount: 10,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
      {
        pitchTypesCount: 3,
        name: 'Touring caravan pitches',
        slug: 'touring-pitches',
        id: '3',
      },
    ],
  },
  {
    name: 'Station Farm',
    region: 'South West England',
    url: '/campsites/England/South_West/Somerset/westhay/station-farm/',
    rating: 10,
    reviewsCount: 22,
    pitchTypes: [
      {
        pitchTypesCount: 10,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
    ],
  },
  {
    name: 'Riverside Camping',
    region: 'North Wales',
    url: '/campsites/Wales/North-Wales/Gwynedd/Caernarfon/riverside-camping/',
    rating: 10,
    reviewsCount: 20,
    pitchTypes: [
      {
        pitchTypesCount: 40,
        name: 'Tent pitches',
        slug: 'tent-pitches',
        id: '4',
      },
      {
        pitchTypesCount: 30,
        name: 'Touring caravan pitches',
        slug: 'touring-pitches',
        id: '3',
      },
      {
        pitchTypesCount: 13,
        name: 'Motorhome pitches',
        slug: 'motorhomes',
        id: '10',
      },
      {
        pitchTypesCount: 6,
        name: 'Lodges, cabins, pods or huts',
        slug: 'lodges-for-hire',
        id: '7',
      },
    ],
  },
  {
    name: 'Nyth Robin',
    region: 'North Wales',
    url: '/campsites/Wales/North-Wales/Gwynedd/Aberdovey/nyth_robin/',
    rating: 10,
    reviewsCount: 20,
    pitchTypes: [
      {
        pitchTypesCount: 9,
        name: 'Touring caravan pitches',
        slug: 'touring-pitches',
        id: '3',
      },
      {
        pitchTypesCount: 9,
        name: 'Motorhome pitches',
        slug: 'motorhomes',
        id: '10',
      },
      {
        pitchTypesCount: 3,
        name: 'Lodges, cabins, pods or huts',
        slug: 'lodges-for-hire',
        id: '7',
      },
      {
        pitchTypesCount: 1,
        name: 'Rent a tent',
        slug: 'rent-a-tent',
        id: '13',
      },
    ],
  },
]

const RegionalWinnersUKData = [
  {
    areaTitle: 'Mid Wales',
    area: 'mid-wales',
    intro:
      "Mid Wales came out on top in terms of overall average rating, gaining a score of 9.5. With the Cambrian Mountains and the Ceredigion coast, it's easy to see why this beautiful spot might top the charts for outdoor holidays.",
    winners: [
      {
        name: 'Sychpwll Centre',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/llandrinio/sychpwll-centre/',
      },
      {
        name: 'Parc Bryndyfi',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/Machynlleth/parc-bryndyfi/',
      },
      {
        name: 'Borders Hideaway Holiday Home Park',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/hay-wye/borders-hideaway-holiday-home-park/',
      },
      {
        name: 'Wild Meadow Camping',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Ceredigion/Plwmp/wild-meadow-camping_/',
      },
      {
        name: 'Meadow Springs Country and Leisure Park',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/caersws/meadow-springs-country-leisure-park/',
      },
      {
        name: 'Pwllyn Farm Camping',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/libanus/pwllyn-farm-camping/',
      },
      {
        name: 'Wern Mill Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Ceredigion/New_Quay/wern_mill_caravan_park/',
      },
      {
        name: 'Riverbend Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/Welshpool/riverbend-caravan-park/',
      },
      {
        name: 'Black Mountain View Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/hay-wye/black-mountain-view-caravan-park/',
      },
      {
        name: 'Smithy Park',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/Montgomery/smithy-caravan-park_/',
      },
    ],
  },
  {
    areaTitle: 'West Wales',
    area: 'west-wales',
    intro:
      'West Wales, scoring a 9.3, is celebrated for its rugged coastlines and sweeping landscapes. From the Pembrokeshire Coast National Park to the beaches of Cardigan Bay, the region is a haven for walkers, wildlife enthusiasts, and those seeking a tranquil seaside getaway.',
    winners: [
      {
        name: 'Oaklea',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/Narberth/oaklea_camp/',
      },
      {
        name: 'Argoed Meadow Camping and Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Carmarthenshire/Newcastle_Emlyn/argoed-meadow-camping-caravan-park/',
      },
      {
        name: 'Folly Farm Holiday Park',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/Kilgetty/folly_farm_holiday_park/',
      },
      {
        name: 'Keeston Camping',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/Haverfordwest/castle-farm/',
      },
      {
        name: 'Church House Farm Getaways',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Carmarthenshire/Carmarthen/church-house-farm/',
      },
      {
        name: 'Glan-Y-Mor Camping',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/Pembroke/glan-y-mor-farm-campsite/',
      },
      {
        name: 'Lleithyr Farm Holiday Park',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/St_Davids/lleithyr-farm-holiday-park/',
      },
      {
        name: 'Gwlad Glamping',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Carmarthenshire/Kidwelly/gwlad-glamping/',
      },
      {
        name: 'Maengwyn Hir Campsite',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/llanfyrnach/maengwyn_hir_campsite/',
      },
      {
        name: 'Laugharne View Caravan Site',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Carmarthenshire/Carmarthen/laugharne-view-caravan-site/',
      },
    ],
  },
  {
    areaTitle: 'North Wales',
    area: 'north-wales',
    intro:
      'Also scoring 9.3, North Wales, home to Eryri National Park and the Clwydian Range, is a walker’s paradise. Visitors will not be disappointed by the views on offer here.',
    winners: [
      {
        name: 'Ceiriog Valley Park',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Denbighshire/Llangollen/ceiriog-valley-park/',
      },
      {
        name: 'Camp Stesion',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Gwynedd/Trawsfynydd/camp-stesion/',
      },
      {
        name: 'Bryn Ednyfed Caravan Site',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Isle_of_Anglesey/Holyhead/bryn_ednyfed_caravan_site/',
      },
      {
        name: 'Llyn Rhys Campsite',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Wrexham/Wrexham/llyn-rhys-campsite/',
      },
      {
        name: 'The Crown Inn and Campsite',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Conwy/llanfihangel-glyn-myfyr/the-crown-inn-and-campsite/',
      },
      {
        name: 'Llain Ffynnon Camping',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Isle_of_Anglesey/Holyhead/llain-ffynnon-camping/',
      },
      {
        name: 'New Inn',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Denbighshire/Llangollen/new-inn/',
      },
      {
        name: 'Llwyn yr Helm Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Gwynedd/Dolgellau/llwyn_yr_helm_caravan_park/',
      },
      {
        name: 'Bryn Goleu Caravan and Camping Site',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Isle_of_Anglesey/Bodedern/bryn-goleu-caravan-and-camping-site/',
      },
      {
        name: 'Maes Carafanau Llanbenwch Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Denbighshire/llanfair_dyffryn_clwyd/maes_carafanau_llanbenwch_caravan_park/',
      },
    ],
  },
  {
    areaTitle: 'the North West',
    area: 'north-west',
    intro:
      'North West England scored 9.2/10 overall in 2024. Looking out over the water of Windemere or up at the rocky peak of Scafell Pike, it’s easy to see why this was crowned the top location in England for outdoor holidays.',
    winners: [
      {
        name: 'Scar Close Camping and Glamping',
        url: 'https://www.pitchup.com/campsites/England/North_West/Lancashire/warton/scar-close-camping-glamping/',
      },
      {
        name: 'Caldbeck Camping',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/Wigton/caldbeck_camping/',
      },
      {
        name: 'Bluebell Wilds',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cheshire/whitchurch/blue-bell-wilds/',
      },
      {
        name: 'Parkgate Farm Holidays',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/Eskdale/parkgate_farm_holidays/',
      },
      {
        name: 'Lamb Cottage Caravan Park',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cheshire/Northwich/lamb-cottage-caravan-park/',
      },
      {
        name: 'Old Kiln Farm',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/Maryport/old-kiln-farm/',
      },
      {
        name: 'Moor House Farm Camping',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/orton/moor-house-farm-camping/',
      },
      {
        name: 'Harbour Side Caravan Site',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/Maryport/harbourside_caravan_site/',
      },
      {
        name: 'Swallow Farm Campsite',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cheshire/Chester/swallow_farm_campsite/',
      },
      {
        name: 'Hollin Head Campsite',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/Eskdale/hollin-head-campsite/',
      },
    ],
  },
  {
    areaTitle: 'Central England',
    area: 'central-england',
    intro:
      'Scoring a 9.2/10 overall, Central England offers a peaceful retreat with plenty of opportunities for outdoor activities. From the history of the Cotswolds to the Peak District’s serene beauty, it provides a relaxing getaway in the heart of England.',
    winners: [
      {
        name: 'Forest of Dean Camping',
        url: 'https://www.pitchup.com/campsites/England/Central/Gloucestershire/Lydney/forest-of-dean-camping/',
      },
      {
        name: 'Pear Tree Cottage at Poplar Farm',
        url: 'https://www.pitchup.com/campsites/England/Central/Lincolnshire/Sutton_on_Sea/pear_tree_cottage_poplar_farm/',
      },
      {
        name: 'Unthank Lane Farm',
        url: 'https://www.pitchup.com/campsites/England/Central/Derbyshire/dronfield/unthank-lane-farm/',
      },
      {
        name: 'Hidden Hideaway',
        url: 'https://www.pitchup.com/campsites/England/Central/Leicestershire/Market_Harborough/hidden-hideaway/',
      },
      {
        name: 'Lindsey Trail Touring Park',
        url: 'https://www.pitchup.com/campsites/England/Central/Lincolnshire/Market_Rasen/lindsey_trail_touring_park/',
      },
      {
        name: 'Orchard Leaze Holiday Park',
        url: 'https://www.pitchup.com/campsites/England/Central/Lincolnshire/Alford/orchard-leaze-holiday-park/',
      },
      {
        name: 'New Farm Holidays',
        url: 'https://www.pitchup.com/campsites/England/Central/Lincolnshire/Boston/new-farm-holidays/',
      },
      {
        name: 'Rockridge',
        url: 'https://www.pitchup.com/campsites/England/Central/Worcestershire/Worcester/rockridge/',
      },
      {
        name: 'Severn Valley Touring Caravan and Camping Site',
        url: 'https://www.pitchup.com/campsites/England/Central/Gloucestershire/newnham/severn_valley_touring_caravan_camping_site/',
      },
      {
        name: 'Langdale Lakes',
        url: 'https://www.pitchup.com/campsites/England/Central/Lincolnshire/langworth/langdale-lakes/',
      },
    ],
  },
  {
    areaTitle: 'the North East',
    area: 'north-east',
    intro:
      "With a 9.2 average score in 2024, the North East of England has a rich heritage and natural beauty. From the historic Hadrian's Wall to the wild Northumberland National Park, the region offers a mix of dramatic landscapes and cultural landmarks.",
    winners: [
      {
        name: 'Draughton Heights Campsite',
        url: 'https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/Skipton/draughton-heights-campsite/',
      },
      {
        name: 'Street Farm Caravan Park',
        url: 'https://www.pitchup.com/campsites/England/North_East/East_Yorkshire/stamford-bridge/street-farm-caravan-park/',
      },
      {
        name: 'Westgate Carr Farm',
        url: 'https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/Pickering/westgate-carr-farm/',
      },
      {
        name: 'Quarry Hill Campsite',
        url: 'https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/Filey/quarry-hill-campsite/',
      },
      {
        name: 'Wild Camping Northumberland',
        url: 'https://www.pitchup.com/campsites/England/North_East/Northumberland/hedley-on-the-hill/wild-camping-northumberland/',
      },
      {
        name: 'Butter Hill Caravan Park',
        url: 'https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/Thirsk/butter-hill-caravan-park/',
      },
      {
        name: 'Abbey Falls Campsite',
        url: 'https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/Ripon/abbey-falls-campsite/',
      },
      {
        name: 'The Spinney Wild Camping',
        url: 'https://www.pitchup.com/campsites/England/North_East/East_Yorkshire/Pocklington/the-spinney-wild-camping/',
      },
      {
        name: 'Bridge House Campsite',
        url: 'https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/Skipton/bridge_house_camp_site/',
      },
      {
        name: 'Old Octon Farm Lodges',
        url: 'https://www.pitchup.com/campsites/England/North_East/East_Yorkshire/Driffield/old-octon-farm/',
      },
    ],
  },
  {
    areaTitle: 'the South West',
    area: 'south-west',
    intro:
      'Also scoring 9.2 overall, the South West of England is renowned for its scenic beaches, rugged cliffs, and the mystical moors of Cornwall and Devon. This region provides a perfect setting for both serene coastal retreats and active adventures.',
    winners: [
      {
        name: 'Combas Farm',
        url: 'https://www.pitchup.com/campsites/England/South_West/Devon/Croyde/combas-farm/',
      },
      {
        name: 'Little Hundreds Campsite',
        url: 'https://www.pitchup.com/campsites/England/South_West/Dorset/tarrant-crawford/little-hundreds-campsite/',
      },
      {
        name: 'Station Farm',
        url: 'https://www.pitchup.com/campsites/England/South_West/Somerset/westhay/station-farm/',
      },
      {
        name: 'Pilsdon View Camping',
        url: 'https://www.pitchup.com/campsites/England/South_West/Dorset/Bridport/pilsdon_view_camping/',
      },
      {
        name: 'Westacott Farm Camping',
        url: 'https://www.pitchup.com/campsites/England/South_West/Devon/Bideford/westacott-farm-camping/',
      },
      {
        name: 'Trigon Farm',
        url: 'https://www.pitchup.com/campsites/England/South_West/Dorset/Wareham/trigon_farm/',
      },
      {
        name: 'Roselidden House Camping',
        url: 'https://www.pitchup.com/campsites/England/South_West/Cornwall/Helston/roselidden-house-camping/',
      },
      {
        name: "Howard's Wood",
        url: 'https://www.pitchup.com/campsites/England/South_West/Dorset/Bridport/howards-wood/',
      },
      {
        name: 'Didwell Farm Campsite',
        url: 'https://www.pitchup.com/campsites/England/South_West/Devon/Kingsbridge/didwell-farm-campsite/',
      },
      {
        name: 'Callestick Camping',
        url: 'https://www.pitchup.com/campsites/England/South_West/Cornwall/Truro/callestick-camping/',
      },
    ],
  },
  {
    areaTitle: 'the South East',
    area: 'south-east',
    intro:
      "With a 9.1 score overall in 2024, the South East of England is a gateway to some of the country's most charming landscapes. From the rolling hills of the South Downs to the vibrant coastal towns, it’s ideal for those seeking both relaxation by the sea and adventures in the countryside.",
    winners: [
      {
        name: 'Little Nash Woods Camping',
        url: 'https://www.pitchup.com/campsites/England/South_East/Kent/Canterbury/little-nash-woods-camping/',
      },
      {
        name: 'Hill Farm Camping',
        url: 'https://www.pitchup.com/campsites/England/South_East/Essex/Braintree/hill-farm-camping/',
      },
      {
        name: 'Duncombe Farm',
        url: 'https://www.pitchup.com/campsites/England/South_East/Hampshire/east-meon/duncombe-farm/',
      },
      {
        name: 'Field View Luxury Glamping',
        url: 'https://www.pitchup.com/campsites/England/South_East/Hampshire/alton/field-view-luxury-glamping/',
      },
      {
        name: 'Deaks Acres',
        url: 'https://www.pitchup.com/campsites/England/South_East/West_Sussex/Haywards_Heath/deaks-acres/',
      },
      {
        name: 'Eyelid Farm',
        url: 'https://www.pitchup.com/campsites/England/South_East/East_Sussex/bodiam/eyelid-farm/',
      },
      {
        name: 'Cae Malwen Farm',
        url: 'https://www.pitchup.com/campsites/England/South_East/Hampshire/Fordingbridge/cae_malwen_farm/',
      },
      {
        name: 'Sparright Farm',
        url: 'https://www.pitchup.com/campsites/England/South_East/West_Sussex/Pulborough/sparright-farm/',
      },
      {
        name: 'Gardners Farm',
        url: 'https://www.pitchup.com/campsites/England/South_East/East_Sussex/ashburnham/gardners-farm/',
      },
      {
        name: 'Fontmills Farm Campsite',
        url: 'https://www.pitchup.com/campsites/England/South_East/East_Sussex/Horam/fontmills-farm-campsite/',
      },
    ],
  },
  {
    areaTitle: 'East Anglia',
    area: 'anglia',
    intro:
      'East Anglia also scored 9.1/10 in 2024, and with its landscape of serene flatlands and picturesque waterways, it’s easy to see why. Renowned for its wildlife-rich wetlands and charming medieval towns, the region offers a peaceful retreat with ample opportunities for bird-watching, boating, and soaking up the local history.',
    winners: [
      {
        name: 'Sunnyside Campsite',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Suffolk/cox-common/sunnyside-campsite/',
      },
      {
        name: 'Strattons Farm Campsite',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/King_s_Lynn/strattons-farm-campsite/',
      },
      {
        name: 'Green Haven Camping',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Suffolk/rumburgh/green-haven-camping/',
      },
      {
        name: 'Brights Farm Camping',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Suffolk/Halesworth/brights-farm-camping/',
      },
      {
        name: 'Bylaugh Country Park',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/Dereham/bylaugh_country_park/',
      },
      {
        name: 'Nether Hall Farm',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Suffolk/Ipswich/nether-hall-farm/',
      },
      {
        name: 'Magpie Meadow',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/North_Walsham/magpie-meadow/',
      },
      {
        name: 'Hayfield Hideaways',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/Mundesley/hayfield-hideaways/',
      },
      {
        name: 'The Croft Campsite',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Suffolk/Halesworth/croft_campsite/',
      },
      {
        name: '7 Ducks',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/King_s_Lynn/7_ducks/',
      },
    ],
  },
  {
    areaTitle: 'Scotland',
    area: 'scotland',
    intro:
      'Scotland scored a 9.1 in 2024 with Pitchup customers. From misty lochs to rolling Highlands, Scotland offers a unique blend of breathtaking scenery and rich cultural heritage, perfect for nature lovers and adventurers alike.',
    winners: [
      {
        name: 'Woodland Gardens Caravan Site',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Fife/Leven/woodland-gardens-caravan-site/',
      },
      {
        name: 'Newton Campsite',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Dumfries_and_Galloway/Gatehouse_of_Fleet/newton-campsite/',
      },
      {
        name: 'Dall Cottage Camping',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Perthshire/Pitlochry/dall_cottage_camping/',
      },
      {
        name: 'Knocknahushoch Farm',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Angus/Kirriemuir/knocknahushoch-farm/',
      },
      {
        name: 'Anwoth Caravan Park',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Dumfries_and_Galloway/Gatehouse_of_Fleet/anwoth-caravan-park/',
      },
      {
        name: 'Kirkton Holidays',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Highlands/Melvich/kirkton-holidays/',
      },
      {
        name: 'Foresterseat Caravan Park',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Angus/Forfar/foresterseat-caravan-park/',
      },
      {
        name: 'Muasdale Holiday Park',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Argyll/Tarbert/muasdale-holiday-park/',
      },
      {
        name: 'Bush of Ewes',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Dumfries_and_Galloway/Langholm/bush-of-ewes/',
      },
      {
        name: "Shepherd's Rest",
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Berwickshire/foulden/shepherds_rest/',
      },
    ],
  },
  {
    areaTitle: 'South Wales',
    area: 'south-wales',
    intro:
      'Home to the creative capital city Cardiff and the serene Wye Valley, South Wales scored a high 9/10 overall from guests in 2023. The perfect blend of outdoor adventure, community and culture, our customers agree that South Wales is a top-notch holiday destination.',
    winners: [
      {
        name: 'Fairoak Lakes',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Monmouthshire/Chepstow/fairoak-lakes/',
      },
      {
        name: 'Brynglas Retreat',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/gwaun_cae_gurwen/brynglas_retreat/',
      },
      {
        name: 'West Ceffyl Glamping',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/Cowbridge/west-ceffyl-campsite/',
      },
      {
        name: 'Parc Y Deri Farm',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Monmouthshire/raglan/parc-y-deri-farm/',
      },
      {
        name: 'Wenallt Wild Camp',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/Neath/wenallt-wild-camp/',
      },
      {
        name: 'Red Sky at Night Campsite',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Monmouthshire/Monmouth/red-sky-at-night-campsite/',
      },
      {
        name: 'Greenways of Gower Premier Leisure Park',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/Swansea/greenways-of-gower-premier-leisure-park/',
      },
      {
        name: "The Hog's Head",
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Monmouthshire/Abergavenny/the-hogs-head/',
      },
      {
        name: 'Hendre Farmhouse Orchard Campsite',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Monmouthshire/Monmouth/hendre-farmhouse-orchard-campsite/',
      },
      {
        name: 'Bryn Bettws Lodge',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/Port_Talbot/bryn-bettws-lodge/',
      },
    ],
  },
]
</script>
<style>
@import url('~/assets/css/review-awards.css');
</style>
